import React, { useState, useEffect } from 'react';
import { useInvitationStore } from '../../../store/invitationStore';
import { Link2, Copy, Trash2, Plus, Clock, Users, Check, ExternalLink } from 'lucide-react';
import CreateInviteModal from '../../invitation/CreateInviteModal';

interface InvitationsTabProps {
  serverId: string;
  serverName: string;
}

const InvitationsTab: React.FC<InvitationsTabProps> = ({ serverId, serverName }) => {
  const { fetchServerInvitations, serverInvitations, revokeInvitation } = useInvitationStore();
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  useEffect(() => {
    fetchServerInvitations(serverId);
  }, [serverId, fetchServerInvitations]);

  const invitations = serverInvitations[serverId] || [];
  const inviteUrlForCode = (code: string) => `https://erozion.net/${code}`;

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(inviteUrlForCode(code));
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleRevoke = async (code: string) => {
    await revokeInvitation(code);
    fetchServerInvitations(serverId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Invitations</h2>
          <p className="text-xs text-text-muted mt-0.5">Manage invite links for your server.</p>
        </div>
        <button
          onClick={() => setShowCreateInvite(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Invite
        </button>
      </div>

      {invitations.length > 0 ? (
        <div className="space-y-2">
          {invitations.map((invite) => (
            <div
              key={invite.code}
              className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-4 flex items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-accent-color/10 flex items-center justify-center flex-shrink-0">
                  <Link2 className="w-4 h-4 text-accent-color" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    erozion.net/{invite.code}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[11px] text-text-muted flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {invite.max_uses > 0 ? `${invite.uses}/${invite.max_uses} uses` : `${invite.uses} uses`}
                    </span>
                    <span className="text-[11px] text-text-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {invite.expires_at
                        ? `Expires ${new Date(invite.expires_at).toLocaleDateString()}`
                        : 'Never expires'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleCopy(invite.code)}
                  className="p-2 rounded-lg hover:bg-highlight/50 text-text-secondary hover:text-text-primary transition-all"
                  title="Copy link"
                >
                  {copiedCode === invite.code ? (
                    <Check className="w-4 h-4 text-success" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => handleRevoke(invite.code)}
                  className="p-2 rounded-lg hover:bg-error/10 text-text-secondary hover:text-error transition-all"
                  title="Revoke"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-accent-color/10 flex items-center justify-center mb-3">
            <Link2 className="w-6 h-6 text-accent-color" />
          </div>
          <p className="text-sm font-medium text-text-primary mb-1">No Invitations</p>
          <p className="text-xs text-text-muted mb-4">Create an invite link to let others join your server.</p>
          <button
            onClick={() => setShowCreateInvite(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-accent-color text-black text-xs font-bold hover:bg-accent-hover transition-all"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Invite
          </button>
        </div>
      )}

      {showCreateInvite && (
        <CreateInviteModal
          serverId={serverId}
          serverName={serverName}
          onClose={() => setShowCreateInvite(false)}
        />
      )}
    </div>
  );
};

export default InvitationsTab;
